/**
 * SOS Alert Modal
 * Emergency popup with flashing animation when SOS message received
 */

import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Chip,
} from '@mui/material';
import {
  ReportProblem as WarningIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  AccessTime as TimeIcon,
  Person as PersonIcon
} from '@mui/icons-material';
import { SOSMessage } from '../services/copAlertsService';
import { getDeviceColor } from '../utils/deviceColors';
import { keyframes } from '@mui/system';

// Flashing animation
const flashAnimation = keyframes`
  0%, 100% { 
    background-color: #f44336;
    box-shadow: 0 0 30px rgba(244, 67, 54, 0.8);
  }
  50% { 
    background-color: #d32f2f;
    box-shadow: 0 0 60px rgba(244, 67, 54, 1);
  }
`;

const pulseAnimation = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.1); }
`;

interface SOSAlertModalProps {
  message: SOSMessage | null;
  onAcknowledge: (messageId: number) => void;
  onViewOnMap?: () => void;
}

const SOSAlertModal: React.FC<SOSAlertModalProps> = ({
  message,
  onAcknowledge,
  onViewOnMap
}) => {
  const [soundPlayed, setSoundPlayed] = useState(false);

  // Play alert sound when modal opens
  useEffect(() => {
    if (message && !soundPlayed) {
      // Play browser notification sound
      try {
        const audio = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBTGH0fPTgjMGHm7A7+OZURE');
        audio.volume = 0.3;
        audio.play().catch(err => console.log('Could not play alert sound:', err));
        setSoundPlayed(true);
      } catch (err) {
        console.log('Audio playback not supported:', err);
      }
    }
  }, [message, soundPlayed]);

  if (!message) return null;

  // Format timestamp - use SMS timestamp (actual send time)
  const formatTimestamp = (msg: SOSMessage): string => {
    try {
      const date = new Date(msg.sms_timestamp);
      return date.toLocaleString();
    } catch {
      return msg.sms_timestamp;
    }
  };

  // Use the shared device color util so the SOS chip matches the map /
  // list / legend for the same device. Student SOSes split by device type;
  // RP/SRP keep their role color.
  const getEntityColor = (msg: Partial<Pick<SOSMessage, 'entity_type' | 'device_type' | 'entity_id'>>): string =>
    getDeviceColor(msg);

  // Parse device UUID from message body (unused)
  // const parseDeviceIdFromMessage = (msg: string): string | null => {
  //   const match = msg.match(/Device ID:\s*([0-9A-Fa-f]{8}-[0-9A-Fa-f]{4}-[0-9A-Fa-f]{4}-[0-9A-Fa-f]{4}-[0-9A-Fa-f]{12})/);
  //   return match ? match[1] : null;
  // };

  // Get sender display name - matches AlertsTab implementation
  const getSenderName = (msg: SOSMessage): string => {
    // Backend now resolves entity_id by matching deviceid against watch_app_id/phone_app_id
    if (msg.entity_id) {
      return msg.entity_id; // e.g., "Student-07", "RP-51"
    }
    
    // Fallback: Show shortened device ID if entity lookup failed
    if (msg.deviceid) {
      if (msg.deviceid.includes('-') && msg.deviceid.length > 20) {
        return `Device ${msg.deviceid.substring(0, 8)}...`;
      }
      return msg.deviceid;
    }
    
    return 'Unknown Device';
  };

  // Display full message text - matches AlertsTab implementation
  const getFullMessage = (msg: string): string => {
    return msg.trim();
  };

  return (
    <Dialog
      open={!!message}
      onClose={() => message && onAcknowledge(message.id)}
      maxWidth="xs"
      PaperProps={{
        sx: {
          bgcolor: '#1a1a1a',
          border: '2px solid #f44336',
          boxShadow: '0 0 30px rgba(244, 67, 54, 0.5)',
          animation: `${flashAnimation} 1.5s ease-in-out infinite`,
          maxWidth: 380,
        }
      }}
    >
      <DialogTitle sx={{
        bgcolor: '#f44336',
        color: 'white',
        display: 'flex',
        alignItems: 'center',
        gap: 0.75,
        py: 1,
        px: 2,
      }}>
        <WarningIcon sx={{
          fontSize: 22,
          animation: `${pulseAnimation} 1s ease-in-out infinite`
        }} />
        <Box component="span" sx={{ fontWeight: 'bold', fontSize: '1rem' }}>
          EMERGENCY SOS ALERT
        </Box>
      </DialogTitle>

      <DialogContent sx={{ bgcolor: '#1a1a1a', pt: 1.5, pb: 1, px: 2, '&.MuiDialogContent-root': { pt: 1.5 } }}>
        {/* Entity + course on one line */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1 }}>
          <PersonIcon sx={{ color: '#999', fontSize: 18 }} />
          <Typography variant="body2" sx={{ color: 'white', fontWeight: 'bold' }}>
            {getSenderName(message)}
          </Typography>
          {message.entity_type && message.comms_direction?.toLowerCase() === 'outgoing' && (
            <Chip
              label={message.entity_type}
              size="small"
              sx={{ bgcolor: getEntityColor(message), color: 'white', fontWeight: 'bold', height: 20, fontSize: '0.7rem' }}
            />
          )}
          {message.course_id && (
            <Typography variant="caption" sx={{ color: '#999', ml: 'auto' }}>
              {message.course_id}
            </Typography>
          )}
        </Box>

        {message.comms_direction?.toLowerCase() === 'incoming' && message.entity_id && (
          <Typography variant="caption" sx={{ color: '#64b5f6', display: 'block', ml: 3.25, mb: 0.5 }}>
            Received by: {message.entity_id} {message.entity_type && `(${message.entity_type})`}
          </Typography>
        )}

        {/* Message Content */}
        <Box sx={{
          bgcolor: '#2d2d2d',
          p: 1.25,
          borderRadius: 1,
          border: '1px solid #f44336',
          mb: 1.25,
        }}>
          <Typography variant="caption" sx={{ color: '#999', display: 'block', mb: 0.25, fontSize: '0.65rem' }}>
            Emergency Message:
          </Typography>
          <Typography variant="body2" sx={{ color: '#ff5252', fontWeight: 'bold', whiteSpace: 'pre-wrap', lineHeight: 1.3 }}>
            {getFullMessage(message.sms_message)}
          </Typography>
        </Box>

        {/* Details — compact inline row */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 0.5 }}>
          {message.sms_number && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <PhoneIcon sx={{ color: '#999', fontSize: 14 }} />
              <Typography variant="caption" sx={{ color: 'white' }}>{message.sms_number}</Typography>
            </Box>
          )}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <TimeIcon sx={{ color: '#999', fontSize: 14 }} />
            <Typography variant="caption" sx={{ color: 'white' }}>{formatTimestamp(message)}</Typography>
          </Box>
          {message.latitude && message.longitude && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <LocationIcon sx={{ color: '#999', fontSize: 14 }} />
              <Typography variant="caption" sx={{ color: 'white' }}>
                {message.latitude.toFixed(4)}, {message.longitude.toFixed(4)}
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ bgcolor: '#1a1a1a', px: 2, py: 1, gap: 1 }}>
        {onViewOnMap && message.latitude && message.longitude && (
          <Button
            onClick={() => { onViewOnMap(); onAcknowledge(message.id); }}
            variant="outlined"
            size="small"
            sx={{
              color: '#64b5f6',
              borderColor: '#64b5f6',
              fontSize: '0.8rem',
              '&:hover': { borderColor: '#90caf9', bgcolor: 'rgba(100, 181, 246, 0.1)' }
            }}
          >
            View on Map
          </Button>
        )}
        <Button
          onClick={() => onAcknowledge(message.id)}
          variant="contained"
          size="small"
          sx={{
            bgcolor: '#f44336',
            color: 'white',
            fontWeight: 'bold',
            fontSize: '0.8rem',
            '&:hover': { bgcolor: '#d32f2f' }
          }}
          autoFocus
        >
          Acknowledge
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SOSAlertModal;

